/**
 * A failed action the page did not tell the person about.
 *
 * Every mutation failure is handed here (query-client.ts). If nothing put an
 * error on screen within GRACE_MS — no showError, no ErrorCard, no role="alert",
 * no red error text, and not the failure's own text anywhere on the page
 * (error-analytics.ts) — the failure was silent, and
 * UnhandledFailureNotice shows it instead. It is reported as error_shown with
 * kind `unhandled_failure`, which doubles as the list of places that still need
 * an error message of their own.
 */

import { failureVisible, noteFailure } from "./error-analytics";

const GRACE_MS = 1_500;

type Listener = (message: string) => void;
const listeners = new Set<Listener>();

export function subscribeUnhandledFailures(fn: Listener): () => void {
  listeners.add(fn);
  return () => {
    listeners.delete(fn);
  };
}

function messageOf(err: unknown): string | null {
  if (err instanceof DOMException && err.name === "AbortError") return null;
  const m = (err as { message?: unknown } | null)?.message;
  return typeof m === "string" && m.trim() ? m.trim() : "The request did not go through. Try again.";
}

export function noteMutationFailed(err: unknown, now: number = Date.now()): void {
  const message = messageOf(err);
  if (message === null) return;
  noteFailure(now);
  setTimeout(() => {
    if (failureVisible(message, now)) return;
    for (const fn of listeners) fn(message);
  }, GRACE_MS);
}
